import assert from "assert";
import { Params, YearStocks, TaxBracket } from "drawdown-web";
import { object, number as rawNumber, array, Schema, boolean } from "yup";

function number() {
  return rawNumber().typeError("A number is required");
}

function nnNumber() {
  return number().min(0, "Must not be negative");
}

const YearStocksSchema = object({
  NormalDeposits: nnNumber().required(),
  NormalGains: nnNumber().required(),
  RothContrib: nnNumber().required(),
  RothUnvested: nnNumber().required(),
  RothEarn: nnNumber().required(),
  Trad: nnNumber().required(),
});

const TaxBracketSchema = object({
  start: number().required(),
  marginal_rate: number().required(),
});

type SpendingRow = {
  income: number;
  total_spending: number;
  ira_eligible_spending: number;
  use_aca: boolean;
};

export type ParamsFromInput = {
  n_adults: number;
  child_births: number[];
  start_age: number;

  initial_stocks: YearStocks;
  spending_table: SpendingRow[];

  interest_rate: number;

  fpl_table: number[];

  standard_deduction: number;
  minor_child_tax_credit_nonrefundible: number;
  adult_child_tax_credit_nonrefundible: number;
  tax_brackets: TaxBracket[];
};

function parseCurency(_: unknown, o: number | string) {
  if (typeof o == "number") {
    return o;
  }
  return Number(o.replace(/,|\$/g, ""));
}

const SpendingCSVRowSchema = object({
  income: number().default(0).transform(parseCurency),
  total_spending: number().required().transform(parseCurency),
  ira_eligible_spending: number().default(0).transform(parseCurency),
  use_aca: boolean().required(),
});

export const SpendingCSVSchema = array().of(SpendingCSVRowSchema);

export const ParamsSchema: Schema<ParamsFromInput> = object({
  n_adults: number().integer().required(),
  child_births: array().of(number().integer().required()).required(),
  start_age: number().integer().required(),

  initial_stocks: YearStocksSchema.required(),
  spending_table: SpendingCSVSchema.required(),

  interest_rate: number().required(),

  fpl_table: array().of(number().required()).required(),

  standard_deduction: number().required(),
  minor_child_tax_credit_nonrefundible: number().required(),
  adult_child_tax_credit_nonrefundible: number().required(),
  tax_brackets: array().of(TaxBracketSchema).required(),
});

export function inputToSimParams(inputForm: ParamsFromInput): Params {
  const { spending_table } = inputForm;
  const spending = spending_table.map((row) => row.total_spending);
  const income = spending_table.map((row) => row.income);
  const ira_eligible_spending = spending_table.map(
    (row) => row.ira_eligible_spending,
  );
  const use_aca = spending_table.map((row) => row.use_aca);
  const n_years = spending_table.length;
  // Actually 401(k) + IRA
  const ira_contribution_limit = 23500 + inputForm.n_adults * 7000
  return {
    ...inputForm,
    n_years,
    spending,
    ira_eligible_spending,
    income,
    use_aca,
    ira_contribution_limit,
  };
}

export function simToInputParams(input: Params): ParamsFromInput {
  const { n_years, spending, ira_eligible_spending, income, use_aca, ...rest } =
    input;
  if (n_years != spending.length) {
    throw "spending length must be n_years";
  }
  if (n_years != ira_eligible_spending.length) {
    throw "ira eligible spending length must be n_years";
  }
  const spending_table = spending.map((total_spending, i) => {
    return {
      income: income[i],
      total_spending,
      ira_eligible_spending: ira_eligible_spending[i],
      use_aca: use_aca[i],
    };
  });
  return { ...rest, spending_table };
}
